import {
  observable,
  computed,
  autorun,
  reaction,
  action,
  runInAction,
  toJS
} from "mobx";

import { selfGuard, noUndef, noDiff } from "../utils";
import { roundTime, ceilTime, distinct } from "../utils";

// !!! TODO: FIX: category display filter messed up !!!

export class SearchForm {
  @computed
  get distinctLocations() {
    return distinct(this.hierarchy.items, o => o.locationId);
  }

  @computed
  get distinctCategories() {
    return distinct(this.hierarchy.items, o => o.categoryId);
  }

  @computed
  get distinctSubcategories() {
    return distinct(this.hierarchy.items, o => o.subcategoryId);
  }

  @computed
  get optLocation() {
    let locFiltered = this.hierarchy.items.filter(
      o =>
        !this.categoryAvailableSystemWide ||
        !this.subcategoryAvailableSystemWide ||
        (o.categoryId === this.categoryAvailableSystemWide &&
          o.subcategoryId === this.subcategoryAvailableSystemWide)
    );
    let locDistinct = distinct(locFiltered, o => o.locationId).map(o => ({
      value: o.locationId,
      text: o.locationName
    }));
    return locDistinct.sort((a, b) => (a.text > b.text) ? 1 : -1);
  }

  @computed
  get optCategory() {
    let catFiltered = this.hierarchy.items.filter(o => {
      return (
        !this.locationAvailableSystemWide ||
        o.locationId === this.locationAvailableSystemWide
      );
    });
    return distinct(catFiltered, o => o.categoryId).map(o => ({
      value: o.categoryId,
      text: o.categoryName
    }));
  }

  optSubcategory(categoryId) {
    let subcatFiltered = this.hierarchy.items.filter(o => {
      if (categoryId && o.categoryId !== categoryId) {
        return false;
      }
      return (
        !this.locationAvailableSystemWide ||
        o.locationId === this.locationAvailableSystemWide
      );
    });
    return distinct(subcatFiltered, o => o.subcategoryId).map(o => ({
      value: o.subcategoryId,
      text: o.subcategoryName
    }));
  }

  @computed
  get onlyOneLocationChoice() {
    return this.optLocation.length === 1;
  }

  @computed
  get onlyOneCategoryAndSubcategoryChoice() {
    return (
      this.optCategory.length === 1 &&
      this.optSubcategory(this.optCategory[0].value).length === 1
    );
  }

  @computed
  get showLocation() {
    return this.distinctLocations.length > 1 && !this.onlyOneLocationChoice;
  }

  @computed
  get showCategory() {
    return (
      (this.distinctCategories.length > 1 ||
        this.distinctSubcategories.length > 1) &&
      (!this.onlyOneCategoryAndSubcategoryChoice || this.onlyOneLocationChoice)
    );
  }

  @computed
  get locationAvailableSystemWide() {
    if (this.distinctLocations.length === 1) {
      return this.distinctLocations[0].locationId;
    }
    return this._location;
  }

  @computed
  get location() {
    if (this.optLocation.length === 1) {
      return this.optLocation[0].value;
    } else {
      return this.locationAvailableSystemWide;
    }
  }

  set location(value) {
    this._location = value;
  }

  @computed
  get categoryAvailableSystemWide() {
    if (this.distinctCategories.length === 1) {
      return this.distinctCategories[0].categoryId;
    }
    return this._category;
  }

  @computed
  get category() {
    if (this.optCategory.length === 1) {
      return this.optCategory[0].value;
    } else {
      return this.categoryAvailableSystemWide;
    }
  }

  set category(value) {
    this._category = value;
  }

  get categoryReal() {
    return this._category;
  }

  @computed
  get subcategoryAvailableSystemWide() {
    if (
      this.distinctCategories.length === 1 &&
      this.distinctSubcategories.length === 1
    ) {
      return this.distinctSubcategories[0].subcategoryId;
    }
    return this._subcategory;
  }

  @computed
  get subcategory() {
    if (
      this.optCategory.length === 1 &&
      this.optSubcategory(this.category).length === 1
    ) {
      return this.optSubcategory(this.category)[0].value;
    } else {
      return this.subcategoryAvailableSystemWide;
    }
  }

  set subcategory(value) {
    this._subcategory = value;
  }

  get subcategoryReal() {
    return this._subcategory;
  }

  @computed
  get optOrgTimeUnit() {
    return this.orgTimeUnit.optOrgTimeUnit;
  }

  @computed
  get locationText() {
    const item = this.optLocation.find(o => o.value === this.location);
    return item && item.text;
  }

  @computed
  get categoryText() {
    const item = this.optCategory.find(o => o.value === this.category);
    return item && item.text;
  }

  @computed
  get subcategoryText() {
    const item = this.optSubcategory().find(o => o.value === this.subcategory);
    return item && item.text;
  }

  @computed
  get timeUnitText() {
    const item = this.optOrgTimeUnit.find(o => o.value === this.timeUnit);
    return item && item.text;
  }

  @action
  incDuration = () => {
    if (this.canIncDuration) {
      this.duration++;
    }
  };

  @action
  decDuration = () => {
    if (this.canDecDuration) {
      this.duration = this.duration - 1;
    }
  };

  @computed
  get canIncDuration() {
    return true;
  }

  @computed
  get canDecDuration() {
    return this.duration > 1;
  }

  @action
  incTimeUnit = () => {
    //debugger
    if (this.canIncTimeUnit) {
      const index = this.optOrgTimeUnit.findIndex(
        o => o.value === this.timeUnit
      );
      if (index > -1) {
        this.timeUnit = this.optOrgTimeUnit[
          index < this.optOrgTimeUnit.length - 1 ? index + 1 : 0
        ].value;
      }
    }
  };

  @action
  decTimeUnit = () => {
    //debugger
    if (this.canDecTimeUnit) {
      const index = this.optOrgTimeUnit.findIndex(
        o => o.value === this.timeUnit
      );
      if (index > -1) {
        this.timeUnit = this.optOrgTimeUnit[
          index > 0 ? index - 1 : this.optOrgTimeUnit.length - 1
        ].value;
      }
    }
  };

  @computed
  get canIncTimeUnit() {
    const index = this.optOrgTimeUnit.findIndex(o => o.value === this.timeUnit);
    return index < this.optOrgTimeUnit.length - 1;
  }

  @computed
  get canDecTimeUnit() {
    const index = this.optOrgTimeUnit.findIndex(o => o.value === this.timeUnit);
    return index > 0;
  }

  @observable _location;
  @observable _category;
  @observable _subcategory;
  @observable _startDate;
  @observable endDate;
  @observable _duration;
  @observable _timeUnit;
  @observable parameters = observable.map({});

  getAppliedConstraintCount(criteria) {
    let count = 0;
    for (let pk of this.parameters.keys()) {
      let pv = this.parameters.get(pk);
      if (pv === undefined) {
        continue;
      }
      if (Array.isArray(toJS(pv))) {
        if (pv.length > 0) {
          count++;
          continue;
        }
      } else {
        let crit = criteria.find(o => o.name === pk) || {};
        if (crit.min !== undefined || crit.max !== undefined) {
          if (crit.min !== pv.min || crit.max !== pv.max) {
            count++;
            continue;
          }
        } else {
          count++;
          continue;
        }
      }
    }
    return count;
  }

  @observable formUpdate;

  constructor(flasher, api, hierarchy, orgTimeUnit, onChange) {
    this.flasher = flasher;
    this.api = api;
    this.hierarchy = hierarchy;
    this.orgTimeUnit = orgTimeUnit;
    this.onChange = onChange;
    this.reactions = [];
  }

  async startOrUpdate({ orgId, orgTimeAtomSecs }) {
    try {
      this.flasher.setOther("Loading.");
      await this.hierarchy.fetch({
        orgId
      });
    } finally {
      this.flasher.setOther();
    }

    this.stopReactions();
    this.reactions.push(
      autorun(async () => {
        try {
          const { startDate, duration, timeUnit } = this;
          if (noUndef([startDate, duration, timeUnit])) {
            this.flasher.setOther("Loading.");
            let endDate = (await this.api.getDateTimeIntervalFromDuration({
              durationTimeUnitId: timeUnit,
              startDate: startDate,
              durationTimeUnitQuantity: duration
            })).endDate;
            //endDate = roundTime(endDate, orgTimeAtomSecs);
            this.endDate = endDate;
          }
        } finally {
          this.flasher.setOther();
        }
      })
    );

    this.reactions.push(
      autorun(() => {
        const {
          location,
          category,
          subcategory,
          startDate,
          endDate,
          duration,
          timeUnit
        } = this;
        this.onChange &&
          this.onChange({
            location,
            category,
            subcategory,
            startDate,
            endDate,
            duration,
            timeUnit
          });
      })
    );
  }

  @action.bound
  stopReactions() {
    this.reactions.forEach(ar => ar());
    this.reactions = [];
  }

  @action.bound
  stop() {
    this.stopReactions();
  }

  @action.bound
  reset({
    location,
    category,
    subcategory,
    startDate,
    endDate,
    duration,
    timeUnit
  }) {
    this.location = location;
    this.category = category;
    this.subcategory = subcategory;
    this.startDate = startDate;
    this.endDate = endDate;
    this.duration = duration;
    this.timeUnit = timeUnit;
  }

  @computed
  get timeUnit() {
    if (
      (!this._timeUnit ||
        !this.optOrgTimeUnit.find(o => o.value === this._timeUnit)) &&
      this.optOrgTimeUnit.length > 0
    ) {
      return undefined;
      return this.optOrgTimeUnit[0].value;
    } else {
      return this._timeUnit;
    }
  }

  set timeUnit(value) {
    this._timeUnit = value;
  }

  get timeUnitReal() {
    return this._timeUnit;
  }

  @computed
  get duration() {
    if (this._duration === undefined) {
      return 1;
    } else {
      return this._duration;
    }
  }

  get durationReal() {
    return this._duration;
  }

  set duration(value) {
    this._duration = value;
  }

  get startDate() {
    return this._startDate;
  }

  set startDate(value) {
    this._startDate = value;
  }

  @action.bound
  changeStartDate(value) {
    this._startDate = value;
    this.endDate = undefined;
  }

  @action.bound
  changeDuration(value) {
    this._duration = value;
    this.endDate = undefined;
  }

  @action.bound
  changeTimeUnit(value) {
    this._timeUnit = value;
    this.endDate = undefined;
  }

  @action.bound
  setParameterItem(parameterName, item, value) {
    if (value) {
      if (this.parameters.has(parameterName)) {
        if (this.parameters.get(parameterName).indexOf(item) === -1) {
          this.parameters.get(parameterName).push(item);
        }
      } else {
        this.parameters.set(parameterName, [item]);
      }
    } else {
      if (this.parameters.has(parameterName)) {
        if (this.parameters.get(parameterName).indexOf(item) !== -1) {
          this.parameters
            .get(parameterName)
            .splice(this.parameters.get(parameterName).indexOf(item), 1);
        }
      }
    }
  }

  @action.bound resetAllParameters() {
    this.parameters = observable.map({});
  }

  hasParameterItem(parameterName, item) {
    if (!this.parameters.has(parameterName)) {
      return false;
    }
    return this.parameters.get(parameterName).indexOf(item) !== -1;
  }

  @computed
  get isSearchAllowed() {
    return this.startDate && this.endDate;
  }
}



// WEBPACK FOOTER //
// ./src/view/SearchForm.js